
<template>
  <div class="fixed inset-0 bg-white bg-opacity-90 backdrop-blur-sm z-50">
    <div class="w-full h-full bg-white flex flex-col">
      <div class="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div class="flex items-center gap-4">
          <button
              @click="confirmExit"
              class="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
          <div class="text-xl font-semibold text-gray-900">{{ testData.test?.title || testData.title || 'Тест' }}</div>
        </div>

        <div class="flex items-center gap-6">
          <div class="text-center">
            <div class="text-sm text-gray-500">Вопрос</div>
            <div class="text-lg font-semibold text-gray-900">{{ currentQuestionIndex + 1 }} / {{ totalQuestions }}</div>
          </div>

          <div class="text-center">
            <div class="text-sm text-gray-500">Осталось времени</div>
            <div class="text-lg font-semibold" :class="timeRemaining <= 300 ? 'text-red-600' : 'text-gray-900'">
              {{ formatTime(timeRemaining) }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div class="max-w-4xl mx-auto p-8">
          <div v-if="currentQuestion" class="space-y-8">
            <div class="bg-white rounded-2xl border border-gray-200 p-8">
              <div class="flex items-center gap-4 mb-6">
                <div class="w-10 h-10 bg-primary rounded-xl flex items-center justify-center text-white font-semibold">
                  {{ currentQuestionIndex + 1 }}
                </div>
                <div class="flex-1">
                  <div v-html="currentQuestion.question" class="prose max-w-none question-content"></div>
                </div>
              </div>

              <div class="text-center text-sm text-gray-500 mb-6 -mt-2">
                <p v-if="isSingleChoice">Выберите один вариант ответа.</p>
                <p v-else>Выберите один или несколько вариантов ответа.</p>
              </div>

              <div class="space-y-3">
                <div
                    v-for="(option, index) in currentQuestion.options"
                    :key="index"
                    class="border border-gray-200 rounded-xl p-4 cursor-pointer transition-all hover:border-blue-300 hover:bg-blue-50"
                    :class="{
                    'border-blue-500 bg-blue-50': selectedAnswers.includes(option),
                    'pointer-events-none opacity-50': isAnswered
                  }"
                    @click="selectAnswer(option)"
                >
                  <div class="flex items-center gap-4">
                    <div
                        class="w-6 h-6 border-2 flex-shrink-0 flex items-center justify-center transition-all"
                        :class="{
                        'rounded-full': isSingleChoice,
                        'rounded-md': !isSingleChoice,
                        'bg-primary border-primary': selectedAnswers.includes(option),
                        'border-gray-300': !selectedAnswers.includes(option)
                      }"
                    >
                      <div
                          v-if="isSingleChoice && selectedAnswers.includes(option)"
                          class="w-3 h-3 bg-white rounded-full"
                      ></div>

                      <svg
                          v-if="!isSingleChoice && selectedAnswers.includes(option)"
                          class="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                    <div class="flex-1 text-gray-900">{{ option }}</div>
                  </div>
                </div>
              </div>

              <div v-if="!isAnswered" class="mt-6">
                <button
                    @click="submitAnswer"
                    :disabled="selectedAnswers.length === 0"
                    class="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Подтвердить ответ
                </button>
              </div>

              <div v-else class="mt-6">
                <button
                    @click="nextQuestion"
                    class="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
                >
                  {{ isLastQuestion ? 'Завершить тест' : 'Следующий вопрос' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-gray-100 h-2">
        <div
            class="bg-primary h-full transition-all duration-300"
            :style="{ width: `${(currentQuestionIndex / totalQuestions) * 100}%` }"
        ></div>
      </div>
    </div>

    <div v-if="showExitConfirm" class="fixed inset-0 bg-white bg-opacity-75 backdrop-blur-sm z-60 flex items-center justify-center">
      <div class="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl border border-gray-200">
        <div class="text-center">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
          </div>
          <div class="text-lg font-semibold text-gray-900 mb-2">Завершить тест?</div>
          <p class="text-gray-600 mb-6">Если вы закроете тест сейчас, попытка будет засчитана как неуспешная.</p>

          <div class="flex gap-3">
            <button
                @click="showExitConfirm = false"
                class="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
                @click="forceExit"
                class="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Завершить
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

import { ref, computed, onMounted, onUnmounted, defineProps, defineEmits } from 'vue'
import apiService from '@/services/api'
import { useToast } from "vue-toastification";

const props = defineProps({
  testData: {
    type: Object,
    required: true
  },
  courseId: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['close', 'complete'])

const toast = useToast();

// Состояние теста
const currentQuestionIndex = ref(0)
const selectedAnswers = ref([])
const isAnswered = ref(false)
const answers = ref([])
const timeRemaining = ref(0)
const timer = ref(null)
const showExitConfirm = ref(false)
const isCompleting = ref(false)

// Отслеживание времени на вопрос
const questionStartTime = ref(null)

// NEW: Реактивное состояние для хранения перемешанных вопросов
const shuffledQuestions = ref([])

// Вычисляемые свойства
// MODIFIED: Теперь вычисляемые свойства работают с перемешанным массивом
const totalQuestions = computed(() => shuffledQuestions.value.length)

const currentQuestion = computed(() => {
  if (shuffledQuestions.value.length > 0 && currentQuestionIndex.value < shuffledQuestions.value.length) {
    return shuffledQuestions.value[currentQuestionIndex.value]
  }
  return null
})

const isSingleChoice = computed(() => {
  if (!currentQuestion.value) {
    return false; // По умолчанию несколько ответов
  }
  // Используем новое поле isSingleAnswer, если оно есть
  if (Object.prototype.hasOwnProperty.call(currentQuestion.value, 'isSingleAnswer')) {
    return currentQuestion.value.isSingleAnswer;
  }
  // Fallback для старых вопросов - проверяем количество правильных ответов
  if (!currentQuestion.value.correctAnswers) {
    return false;
  }
  return currentQuestion.value.correctAnswers.length === 1;
});

const isLastQuestion = computed(() => currentQuestionIndex.value === totalQuestions.value - 1)

// Инициализация таймера
const initTimer = () => {
  const startTime = new Date(props.testData.attempt.startedAt).getTime()
  const timeLimit = (props.testData?.test?.timeLimit || props.testData?.timeLimit || 30) * 60 * 1000

  const updateTimer = () => {
    const now = Date.now()
    const elapsed = now - startTime
    const remaining = Math.max(0, timeLimit - elapsed)

    timeRemaining.value = Math.floor(remaining / 1000)

    if (remaining <= 0) {
      finishTest('timeout')
    }
  }

  updateTimer()
  timer.value = setInterval(updateTimer, 1000)
}

// Форматирование времени
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// Выбор ответа
const selectAnswer = (answer) => {
  if (isAnswered.value) return;

  if (isSingleChoice.value) {
    selectedAnswers.value = [answer];
  } else {
    const index = selectedAnswers.value.indexOf(answer);
    if (index > -1) {
      selectedAnswers.value.splice(index, 1);
    } else {
      selectedAnswers.value.push(answer);
    }
  }
};

// Подтверждение ответа
const submitAnswer = async () => {
  if (selectedAnswers.value.length === 0) return

  try {
    const testId = props.testData?.test?.id || props.testData?.id
    
    // Вычисляем время, затраченное на вопрос (в секундах)
    const timeSpent = questionStartTime.value ? Math.floor((Date.now() - questionStartTime.value) / 1000) : 0
    
    const response = await apiService.answerQuestion(props.courseId, testId, {
      questionId: currentQuestion.value.id,
      userAnswers: [...selectedAnswers.value],
      timeSpent: timeSpent
    })

    answers.value[currentQuestionIndex.value] = [...selectedAnswers.value]
    isAnswered.value = true

    if (currentQuestion.value.isCritical && response && response.isCorrect === false) {
      finishTest('failed')
      return
    }

  } catch (error) {
    console.error('Ошибка при отправке ответа:', error)
    toast.error('Ошибка при отправке ответа')
  }
}

// Инициализация времени начала вопроса
const startQuestionTimer = () => {
  questionStartTime.value = Date.now()
}

// Переход к следующему вопросу
const nextQuestion = () => {
  if (isLastQuestion.value) {
    finishTest('completed')
  } else {
    currentQuestionIndex.value++
    selectedAnswers.value = []
    isAnswered.value = false
    // Запускаем таймер для нового вопроса
    startQuestionTimer()
  }
}

// Завершение теста
/**
 * Завершает тест и отправляет результат на бэкенд.
 * @param {('completed'|'abandoned'|'timeout'|'failed')} reason - Причина завершения теста.
 */
const finishTest = async (reason) => {
  if (isCompleting.value) return;
  isCompleting.value = true;

  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }

  try {
    const testId = props.testData?.test?.id || props.testData?.id;
    const timeLimitSec = (props.testData?.test?.timeLimit || props.testData?.timeLimit || 30) * 60;

    // Вычисляем затраченное время на основе оставшегося
    const timeSpent = timeLimitSec - timeRemaining.value;

    // Готовим данные для отправки на бэкенд
    const payload = {
      reason: reason,
      timeSpent: Math.max(0, timeSpent) // Убедимся, что время не отрицательное
    };

    if (typeof window !== 'undefined' && window.BX24 && typeof window.BX24.getAuth === 'function') {
      try {
        const authData = window.BX24.getAuth()
        const userAccessToken = authData?.access_token || authData?.AUTH_ID || authData?.auth_id
        if (userAccessToken) payload.userAccessToken = userAccessToken
      } catch (e) {
        console.warn('Не удалось получить токен пользователя через BX24:', e)
      }
    }

    // Вызываем обновленный метод API
    const response = await apiService.completeTest(props.courseId, testId, payload);

    emit('complete', {
      ...response,
      reason: reason,
      testId: testId,
      courseId: props.courseId
    });

  } catch (error) {
    console.error('Ошибка при завершении теста:', error);
    toast.error('Ошибка при завершении теста')
  } finally {
    isCompleting.value = false;
  }
}

const confirmExit = () => {
  showExitConfirm.value = true
}

const forceExit = async () => {
  showExitConfirm.value = false
  try {
    await finishTest('abandoned')
  } catch (error) {
    console.error('Ошибка при завершении покинутого теста:', error)
  }
  emit('close')
}

// NEW: Функция для перемешивания массива (алгоритм Fisher-Yates)
const shuffleArray = (array) => {
  const arr = [...array]; // создаём копию
  let currentIndex = arr.length, randomIndex;

  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [arr[currentIndex], arr[randomIndex]] = [arr[randomIndex], arr[currentIndex]];
  }
  return arr;
};

// Инициализация
onMounted(() => {
  initTimer()

  // NEW: Логика перемешивания вопросов при инициализации
  const originalQuestions = [...(props.testData?.test?.questions || props.testData?.questions || [])];
  const startIndex = props.testData?.attempt?.nextQuestionIndex || 0;

  if (originalQuestions.length > 0) {
    if (startIndex > 0 && startIndex < originalQuestions.length) {
      // Есть прогресс. Перемешиваем только непройденные вопросы.
      const answeredPart = originalQuestions.slice(0, startIndex);
      const unansweredPart = originalQuestions.slice(startIndex);

      console.log(unansweredPart)
      const shuffledUnansweredPart = shuffleArray(unansweredPart);
      console.log(shuffledUnansweredPart)
      shuffledQuestions.value = [...answeredPart, ...shuffledUnansweredPart];
    } else {
      // Новый тест. Перемешиваем все вопросы.
      shuffledQuestions.value = shuffleArray(originalQuestions);
    }
  }

  // Устанавливаем начальный вопрос если есть сохраненный прогресс
  if (startIndex > 0) {
    currentQuestionIndex.value = startIndex;
  }
  
  // Запускаем таймер для первого вопроса
  startQuestionTimer()
})

// Очистка таймера
onUnmounted(() => {
  if (timer.value) {
    clearInterval(timer.value)
  }
})
</script>

<style scoped>
.z-60 {
  z-index: 60;
}

/* Стили для содержимого вопроса */
:deep(.question-content) {
  line-height: 1.6;
  color: #374151;
}

:deep(.question-content) h1,
:deep(.question-content) h2,
:deep(.question-content) h3 {
  margin: 1rem 0 0.5rem 0;
  color: #111827;
}

:deep(.question-content) p {
  margin-bottom: 0;
}

:deep(.question-content) ul,
:deep(.question-content) ol {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

/* Стили для цитат */
:deep(.question-content) blockquote,
:deep(.question-content) .ql-blockquote {
  border-left: 4px solid #574AEA;
  margin: 1rem 0;
  padding: 12px 12px 12px 1rem;
  font-style: italic;
  color: #6b7280;
  background: rgba(87, 74, 234, 0.05);
  border-radius: 0 8px 8px 0;
  position: relative;
}

:deep(.question-content) blockquote::before,
:deep(.question-content) .ql-blockquote::before {
  content: '"';
  font-size: 3rem;
  color: #574AEA;
  position: absolute;
  top: -10px;
  left: 10px;
  opacity: 0.3;
  font-family: serif;
}

/* Стили для таблиц */
:deep(.question-content) table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: 20px 0;
  color: #202020;
  font-size: 14px;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.question-content) table th,
:deep(.question-content) table td {
  padding: 12px 15px;
  text-align: left;
  border-right: 1px solid #e1e1e1;
  border-bottom: 1px solid #e1e1e1;
}

:deep(.question-content) table th {
  background-color: #f8f9fa;
  font-weight: bold;
  color: #333;
}

:deep(.question-content) table th:last-child,
:deep(.question-content) table td:last-child {
  border-right: none;
}

:deep(.question-content) table tr:last-child td {
  border-bottom: none;
}

:deep(.question-content) table tr:nth-child(even) {
  background-color: #f9f9f9;
}

:deep(.question-content) table tr:hover {
  background-color: #f0f0f0;
}

/* Стили для размеров шрифта */
:deep(.question-content) .ql-size-small {
  font-size: 0.75em;
}

:deep(.question-content) .ql-size-large {
  font-size: 1.5em;
}

:deep(.question-content) .ql-size-huge {
  font-size: 2.5em;
}

/* Стили для выравнивания */
:deep(.question-content) .ql-align-left {
  text-align: left;
}

:deep(.question-content) .ql-align-center {
  text-align: center;
}

:deep(.question-content) .ql-align-right {
  text-align: right;
}

:deep(.question-content) .ql-align-justify {
  text-align: justify;
}

/* Стили для кода */
:deep(.question-content) code {
  background: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-family: monospace;
  font-size: 0.875rem;
}

:deep(.question-content) pre {
  background: #1f2937;
  color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 0.5rem 0;
}

/* Стили для изображений */
:deep(.question-content) img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 1rem 0;
}

/* Emoji стили */
:deep(.question-content) .ql-emoji {
  height: 1.2em;
  width: auto;
  vertical-align: middle;
  display: inline-block;
}

/* Стили для ссылок */
:deep(.question-content) a {
  color: #574AEA;
  text-decoration: underline;
}

:deep(.question-content) a:hover {
  color: #473add;
}

/* Мобильная адаптивность */
@media (max-width: 768px) {
  /* Основной контейнер */
  .fixed.inset-0 {
    padding: 0;
  }

  .h-full.flex.flex-col {
    height: 100vh;
  }

  /* Header */
  .bg-white.shadow-sm.z-10 {
    padding: 12px 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .flex.items-center.justify-between {
    gap: 12px;
  }

  /* Прогресс и информация */
  .flex.items-center.gap-8 {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }

  .flex.items-center.gap-4 {
    gap: 8px;
    justify-content: center;
  }

  .text-lg {
    font-size: 1rem;
  }

  .text-sm {
    font-size: 0.8rem;
  }

  /* Прогресс бар */
  .bg-gray-200.rounded-full.h-2 {
    height: 6px;
    margin: 8px 0;
  }

  /* Кнопка выхода */
  .p-2 {
    padding: 8px;
  }

  /* Основной контент */
  .flex-1.overflow-y-auto {
    padding: 0;
  }

  .max-w-4xl.mx-auto.p-8 {
    max-width: 100%;
    padding: 16px;
    margin: 0;
  }

  /* Карточка вопроса */
  .bg-white.rounded-2xl.border.border-gray-200.p-8 {
    padding: 16px;
    border-radius: 16px;
    margin-bottom: 16px;
  }

  .flex.items-start.gap-4 {
    gap: 12px;
  }

  .w-10.h-10 {
    width: 32px;
    height: 32px;
    font-size: 0.875rem;
  }

  /* Варианты ответов */
  .space-y-3 {
    gap: 12px;
  }

  .space-y-3 > * + * {
    margin-top: 12px;
  }

  .border.border-gray-200.rounded-xl.p-4 {
    padding: 12px;
    border-radius: 12px;
  }

  .w-6.h-6 {
    width: 20px;
    height: 20px;
  }

  .w-3.h-3 {
    width: 12px;
    height: 12px;
  }

  /* Кнопки действий */
  .mt-6.flex.gap-4 {
    margin-top: 16px;
    flex-direction: column;
    gap: 12px;
  }

  .px-8.py-3 {
    padding: 14px 16px;
    font-size: 0.875rem;
    min-height: 48px;
    border-radius: 12px;
  }

  /* Модальное окно подтверждения */
  .bg-white.rounded-2xl.max-w-md.mx-4 {
    max-width: calc(100vw - 32px);
    margin: 16px;
    border-radius: 16px;
  }

  .p-8 {
    padding: 16px;
  }

  .w-16.h-16 {
    width: 48px;
    height: 48px;
  }

  .mb-4 {
    margin-bottom: 12px;
  }

  .mb-6 {
    margin-bottom: 16px;
  }

  /* Спиннер загрузки */
  .w-5.h-5 {
    width: 16px;
    height: 16px;
  }

  /* Адаптация контента вопроса */
  :deep(.question-content) {
    font-size: 0.875rem;
    line-height: 1.5;
  }

  :deep(.question-content) pre {
    padding: 0.5rem;
    font-size: 0.75rem;
    overflow-x: auto;
  }

  :deep(.question-content) table {
    font-size: 12px;
  }

  :deep(.question-content) table th,
  :deep(.question-content) table td {
    padding: 8px 10px;
  }

  :deep(.question-content) blockquote,
  :deep(.question-content) .ql-blockquote {
    padding-left: 0.5rem;
    margin: 0.5rem 0;
  }

  :deep(.question-content) blockquote::before,
  :deep(.question-content) .ql-blockquote::before {
    font-size: 2rem;
    top: -5px;
    left: 5px;
  }
}

@media (max-width: 480px) {
  /* Дополнительная компактность для маленьких экранов */
  .bg-white.shadow-sm.z-10 {
    padding: 8px 12px;
  }

  .max-w-4xl.mx-auto.p-8 {
    padding: 12px;
  }

  .bg-white.rounded-2xl.border.border-gray-200.p-8 {
    padding: 12px;
    border-radius: 12px;
  }

  .flex.items-start.gap-4 {
    gap: 8px;
    flex-direction: column;
    align-items: stretch;
  }

  .w-10.h-10 {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
    align-self: flex-start;
  }

  .border.border-gray-200.rounded-xl.p-4 {
    padding: 8px;
  }

  .px-8.py-3 {
    padding: 12px 16px;
    font-size: 0.8rem;
    min-height: 44px;
  }

  .text-lg {
    font-size: 0.9rem;
  }

  .text-sm {
    font-size: 0.75rem;
  }

  /* Адаптация контента вопроса */
  :deep(.question-content) {
    font-size: 0.875rem;
    line-height: 1.5;
  }

  :deep(.question-content) pre {
    padding: 0.5rem;
    font-size: 0.75rem;
    overflow-x: auto;
  }
}
</style>
